import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.js';

/**
 * 工厂端退出登录。
 *
 * ── 后端不用新增东西 ────────────────────────────────────────────
 *
 * 租户 session 和内部 session 是**同一个 cookie**（`genbox_session`，同一把
 * JWT_SECRET 签的，只是 claims 里多了 `tenant_id`）。所以 `POST /api/auth/logout`
 * 清 cookie 这一条对两边都成立，不需要为工厂端再开一个端点。
 *
 * ── 退到 `/login`，不是 `/portal/login` ─────────────────────────
 *
 * 路由表里只有一条 `/login`，画哪个登录页**按域名分**（`isPortalHost()`）——
 * 在 portal.link4a.com 上它就是工厂端登录页。
 *
 * 这一条跟「身份从 session 不从域名」那句话不矛盾：那句只对**已登录之后**。
 * 退出之后 session 没了，能用来分流的就只剩域名。写死 `/portal/login` 反而会
 * 撞上一条不存在的路由。
 *
 * ── 一定要清 react-query 缓存 ────────────────────────────────────
 *
 * 不清的话，下一个人在同一台设备上登进来（工厂前台那台公用电脑就是这么用的），
 * 会先看到上一个账号的客户列表——缓存还在，要等接口回来才刷掉。
 * 那几百毫秒里显示的是别人的客户数据。
 */
export function usePortalLogout(): { logout: () => Promise<void>; pending: boolean } {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);

  const logout = async (): Promise<void> => {
    setPending(true);
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // 网络挂了也要往下走：cookie 清不掉的话下面 refresh 会发现还登着，
      // 至少不能把人卡在一个点了没反应的按钮上
    }
    qc.clear();
    await refresh();
    setPending(false);
    navigate('/login', { replace: true });
  };

  return { logout, pending };
}
